import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../components/loading/loading.component";
import GlimpseHeader from "../../components/career-header/career-header.component";
import CareerIntro from "components/career-intro/career-intro";
import Button from "components/button/button";
import { useEffect, useRef, useState } from "react";
import { useLayout } from "contexts/LayoutContext";
import { archiveCareer, getCareerById } from "services/career.service";
import GlimpsePopup from "components/popup/popup.component";
import {
  CareerTrends,
  DayinLife,
  DidYouKnow,
  Impacts,
  PageNumberState,
} from "interfaces/CareerInfoInterface";
import { displayError, displaySuccess } from "components/Toast/toast.component";
import { useSelector } from "react-redux";
import {
  CareerDetailsInfo,
  IDeeperDiveEducations,
  IDeeperDiveProsCons,
  IExpertVoicesString,
} from "interfaces/CareerInterface";
import { References } from "enums/glimps.enums";
import ReactPlayer from "react-player";
import BarChart from "components/charts/bar-chart.component";
import InfoCard from "components/card/info-card";
import { VIDEO_PLAYER_CONFIG } from "utills/video";
import { noPreviewImage } from "utills/image";
import { sortByOrder } from "utills/sort";


const CareerInfoScreen: React.FC = () => {
  const { isChatOpen } = useLayout();
  let { param } = useParams();

  const videoRef = useRef<ReactPlayer>(null);
  const { userInfo } = useSelector((state: any) => state.auth);
  const [selectedVideo, setSelectedVideo] = useState<IExpertVoicesString>();
  const [enableDayInLife, setEnableDayInLife] = useState<boolean>(false);
  const [enableDidYouKnow, setEnableDidYouKnow] = useState<boolean>(false);
  const [enableEducation, setEnableEducation] = useState<boolean>(false);
  const [enableProsCons, setEnableProsCons] = useState<boolean>(false);
  let navigate = useNavigate();
  const [popup, setPopup] = useState<boolean>(false);
  const [archiveAlert, setArchiveAlert] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<PageNumberState>({
    dayInLife: 0,
    didyouknow: 0,
    education: 0,
    prosCons: 0,
  });
  const { data: careerInfo, isLoading } = useQuery({
    queryKey: ["GetCareerById", param],
    queryFn: () => {
      return getCareerById(param as string);
    },
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const handleArchiveCareer = useMutation({
    mutationFn: (data: CareerDetailsInfo) => archiveCareer(data),
    retry: 0,
    onSuccess: () => {
      displaySuccess("Career has been archived successfully.");
    },
    onError: () => {
      displayError("Unable to archive career.");
    },
  });

  const handleArchiveCareerSubmit = (data: CareerDetailsInfo) => {
    handleArchiveCareer.mutateAsync(data);
  };

  useEffect(() => {
    if (careerInfo?.expert_voices.length) {
      setSelectedVideo(careerInfo?.expert_voices[0]);
    }
  }, [careerInfo?.expert_voices]);

  return (
    <div className="w-full">
      {isLoading ? (
        <Loading />
      ) : careerInfo ? (
        <div className="flex flex-col w-full gap-6">
          {/* Top Bar: Back + Admin Actions */}
          <div className="flex justify-between items-center">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            {userInfo.user.role === "super" && (
              <div className="flex gap-2">
                {careerInfo.status === "published" && (
                  <Button variant="destructive" onClick={() => setArchiveAlert(true)}>
                    Archive Career
                  </Button>
                )}
                <Button variant="primary" onClick={() => navigate(`/careers/details/${param as string}`)}>
                  Edit Career
                </Button>
              </div>
            )}
          </div>

          {/* Career Header */}
          <CareerIntro
            title={careerInfo?.name ?? ""}
            subtitle={careerInfo?.superpower}
            description={careerInfo?.description}
            image={careerInfo?.icon_url || noPreviewImage}
          />

          {/* Archive Modal */}
          <GlimpsePopup
            title=""
            onClose={() => {
              setArchiveAlert(false);
            }}
            showCross={false}
            popup={archiveAlert}
            customClass="max-w-[347px]"
          >
            <div className="text-dark-blue text-[20px] flex items-start">
              Archive
              <span className="px-1 font-bold"> {careerInfo.name}?</span>
            </div>
            <div className="text-sm text-gray-dark">
              New users are not able to see archived careers. Already existing
              users can still see and interact with it on their dashboard. All
              career data will be preserved.
            </div>
            <div className="flex justify-between mt-5">
              <Button variant="outline" onClick={() => setArchiveAlert(false)}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handleArchiveCareerSubmit(careerInfo);
                  setArchiveAlert(false);
                  navigate(-1);
                }}
              >
                Confirm
              </Button>
            </div>
          </GlimpsePopup>

          {/* Row 1: Expert Voices | Contribution | (Salary when chat closed) */}
          <div className={`grid grid-cols-1 gap-6 ${isChatOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
            <InfoCard title="Expert Voices" className="min-h-[340px]">
              <div className="flex items-end justify-center gap-3">
                {careerInfo.expert_voices.length > 1 && (
                  <div className="flex flex-col justify-end">
                    {careerInfo.expert_voices.map((item, index) => (
                      <div
                        key={index}
                        className={`${
                          selectedVideo?.voice_url === item.voice_url
                            ? "opacity-100 border-2 border-dark-blue"
                            : "opacity-50"
                        } block mb-1 rounded-md cursor-pointer`}
                      >
                        <img
                          src={
                            item.voice_thumbnail
                              ? item.voice_thumbnail
                              : noPreviewImage
                          }
                          alt="Expert"
                          className="max-w-[50px] max-h-[70px]"
                          onClick={() => setSelectedVideo(item)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="rounded border border-gray-light overflow-hidden w-[152px] h-[268px]" onContextMenu={(e) => e.preventDefault()}>
                  <ReactPlayer
                    ref={videoRef}
                    url={selectedVideo?.voice_url as string}
                    light={selectedVideo?.voice_thumbnail as string}
                    playing={true}
                    controls
                    config={VIDEO_PLAYER_CONFIG}
                    width="100%"
                    height="100%"
                  />
                </div>
              </div>
            </InfoCard>

            <InfoCard title="Contribution" colSpan={2} className="min-h-[340px]">
              <div className="w-full h-[268px] rounded border border-gray-light bg-white p-3">
                <img
                  src={careerInfo?.contribution_url || noPreviewImage}
                  className="w-full h-full object-contain"
                  alt="Contribution"
                />
              </div>
            </InfoCard>

            {!isChatOpen && (
              <InfoCard title="Salary" titleAlign="center" contentAlign="center" className="min-h-[340px]">
                <div className="w-full h-[268px] rounded border border-gray-light bg-white p-3 flex items-center justify-center">
                  <BarChart
                    data={{
                      datasets: [{
                        label: "Salary",
                        data: [
                          careerInfo.salaries.starting ? Number(careerInfo.salaries.starting) : 0,
                          careerInfo.salaries.median ? Number(careerInfo.salaries.median) : 0,
                          careerInfo.salaries.advance ? Number(careerInfo.salaries.advance) : 0,
                        ],
                        backgroundColor: ["#D4B07D", "#F4CC94", "#FFD497"],
                        borderRadius: { topLeft: 8, topRight: 8 },
                      }],
                      labels: [["Low", "(Starting)"], ["Median", ""], ["High", "(Experienced)"]],
                    }}
                    salaryChart={true}
                  />
                </div>
              </InfoCard>
            )}
          </div>

          {/* Rows 2-4: Career Trends, Pros, Cons, Day In The Life, Did You Know, Education, Impact */}
          <div className={`grid grid-cols-1 gap-6 ${isChatOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
            <InfoCard title="Career Trends" titleAlign="center" contentAlign="top">
              <div className="flex flex-col gap-4 text-center">
                {(careerInfo?.trends as CareerTrends[])?.map((item, index) => (
                  <div key={index}>
                    <p className="text-2xl font-bold text-gray-dark">{item.title}</p>
                    <p className="text-xs font-normal text-gray-dark">{item.description}</p>
                  </div>
                ))}
              </div>
            </InfoCard>

            <InfoCard title="Pros" titleAlign="center" contentAlign="top">
              <div className="flex flex-col gap-1 text-center">
                {(careerInfo?.pros_cons as IDeeperDiveProsCons)?.pros?.map((item, index) => (
                  <p key={index} className="text-sm font-normal text-gray-dark">{item.title}</p>
                ))}
              </div>
            </InfoCard>

            <InfoCard title="Cons" titleAlign="center" contentAlign="top">
              <div className="flex flex-col gap-1 text-center">
                {(careerInfo?.pros_cons as IDeeperDiveProsCons)?.cons?.map((item, index) => (
                  <p key={index} className="text-sm font-normal text-gray-dark">{item.title}</p>
                ))}
              </div>
            </InfoCard>

            {/* Day In The Life: spans rows when chat closed, inline when chat open */}
            {!isChatOpen && (
              <InfoCard title="Day In The Life" titleAlign="center" rowSpan={3} contentAlign="top">
                <div className="flex flex-col gap-8 w-full items-center">
                  {sortByOrder(careerInfo?.days_in_life as DayinLife[] ?? []).map((item, index) => (
                      <div key={index} className="flex flex-col items-center text-center">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-[50px] h-[50px] rounded-full mb-1"
                        />
                        <p className="text-sm font-semibold text-gray-dark">{item.title}</p>
                        <p className="text-xs font-normal max-w-[180px] text-black-50">{item.description}</p>
                      </div>
                    ))}
                </div>
              </InfoCard>
            )}

            {/* Did You Know */}
            <InfoCard title="Did You Know?" colSpan={3} contentAlign="center" className="!p-6">
              <div className="flex flex-wrap gap-5 justify-around w-full">
                {(careerInfo?.did_you_knows as DidYouKnow[])?.map((item, index) => (
                  <div key={index} className="flex flex-col items-center text-center w-[178px] gap-1.5">
                    <img src={item.icon_url} alt={item.title} className="w-[50px] h-[50px]" />
                    <p className="text-xs font-normal text-gray-dark">{item.title}</p>
                  </div>
                ))}
              </div>
            </InfoCard>

            {/* Education | Impact On Others — equal width */}
            <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <InfoCard title="Education" contentAlign="top">
                <div className="grid grid-cols-2 gap-x-2 gap-y-6 w-full justify-items-center">
                  {sortByOrder(careerInfo?.educations as IDeeperDiveEducations[] ?? []).map((item, index) => (
                      <div key={index} className={`flex flex-col items-center text-center gap-0.5 ${
                        sortByOrder(careerInfo?.educations as IDeeperDiveEducations[] ?? []).length % 2 !== 0 &&
                        index === sortByOrder(careerInfo?.educations as IDeeperDiveEducations[] ?? []).length - 1
                          ? 'col-span-2' : ''
                      }`}>
                        <div className="w-[60px] h-[60px] rounded-full border border-gray-light bg-white p-1.5 flex items-center justify-center mb-1">
                          <img
                            src={
                              item.label
                                ? `/assets/icons/${item.label.replaceAll(" ", "_")}.png`
                                : "/assets/icons/EducationPlaceholder.png"
                            }
                            alt={item.label}
                            className="w-full h-full object-contain"
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null;
                              currentTarget.src = "/assets/icons/Certification.png";
                            }}
                          />
                        </div>
                        <p className="text-sm font-semibold text-gray-dark">{item.title}</p>
                        <p className="text-xs font-normal capitalize text-black-50">{item.type}</p>
                        <p className="text-xs font-normal text-black-50">
                          {item.duration ? Math.round(Number(item.duration)) : ''} {item.unit}
                        </p>
                      </div>
                    ))}
                </div>
              </InfoCard>

              <InfoCard title="Impact On Others" contentAlign="center">
                <div className="flex flex-col gap-6 w-full">
                  {(careerInfo?.impacts as Impacts[])?.map((item, index) => (
                    <div key={index}>
                      <p className="text-base font-semibold text-gray-dark leading-6">"{item.description}"</p>
                      <p className="text-base font-normal text-gray-medium text-right mt-1">{item.author}</p>
                    </div>
                  ))}
                </div>
              </InfoCard>
            </div>
          </div>

          {/* Day In The Life + Salary — shown at bottom when chat is open */}
          {isChatOpen && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <InfoCard title="Day In The Life" titleAlign="center" colSpan={2} contentAlign="top">
                <div className="grid grid-cols-3 gap-x-4 gap-y-6 w-full justify-items-center">
                  {sortByOrder(careerInfo?.days_in_life as DayinLife[] ?? []).map((item, index) => (
                      <div key={index} className="flex flex-col items-center text-center">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-[50px] h-[50px] rounded-full mb-1"
                        />
                        <p className="text-sm font-semibold text-gray-dark">{item.title}</p>
                        <p className="text-xs font-normal max-w-[180px] text-black-50">{item.description}</p>
                      </div>
                    ))}
                </div>
              </InfoCard>

              <InfoCard title="Salary" titleAlign="center" contentAlign="center">
                <div className="w-full h-[268px] rounded border border-gray-light bg-white p-3 flex items-center justify-center">
                  <BarChart
                    data={{
                      datasets: [{
                        label: "Salary",
                        data: [
                          careerInfo.salaries.starting ? Number(careerInfo.salaries.starting) : 0,
                          careerInfo.salaries.median ? Number(careerInfo.salaries.median) : 0,
                          careerInfo.salaries.advance ? Number(careerInfo.salaries.advance) : 0,
                        ],
                        backgroundColor: ["#D4B07D", "#F4CC94", "#FFD497"],
                        borderRadius: { topLeft: 8, topRight: 8 },
                      }],
                      labels: [["Low", "(Starting)"], ["Median", ""], ["High", "(Experienced)"]],
                    }}
                    salaryChart={true}
                  />
                </div>
              </InfoCard>
            </div>
          )}

          {/* View Sources */}
          <div className="text-sm">
            <button
              type="button"
              className="cursor-pointer font-semibold text-primary"
              onClick={() => setPopup(true)}
              aria-haspopup="dialog"
            >
              View sources
            </button>
          </div>

          {/* Sources Popup */}
          <GlimpsePopup
            title=""
            popup={popup}
            onClose={() => setPopup(false)}
            showCross={true}
            customClass="max-w-[480px]"
          >
            <>
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-dark-blue mb-2">
                    {careerInfo.name}: Sources
                  </h2>
                  <p className="text-sm font-normal leading-5 text-gray-dark">
                    Listed below are sources we used to gather data about this career.
                  </p>
                </div>
                {[
                  { label: "Did You Know", type: References.DidYouKnow },
                  { label: "Salary", type: References.Salary },
                  { label: "Career Demand", type: References.CareerDemand },
                  { label: "Impact Quote", type: References.ImpactQuote },
                ].map(({ label, type }) => {
                  const refs = careerInfo.references.filter(
                    (x) => x.title === type && x.link
                  );
                  if (refs.length === 0) return null;
                  return (
                    <div key={label} className="mb-4">
                      <p className="text-base font-semibold text-dark-blue mb-1">{label}</p>
                      <div className="flex flex-col gap-1">
                        {refs.map((item, i) => (
                          <a
                            key={i}
                            href={item.link as string}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-primary break-all"
                          >
                            {item.link}
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          </GlimpsePopup>
        </div>
      ) : (
        <Loading text="Career Not found" />
      )}
    </div>
  );
};

export default CareerInfoScreen;
